import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { RotateCcw } from "lucide-react";
import { useTranslation } from "react-i18next";
import tw from "twin.macro";

import { ModuleCommandEnum } from "@/api/events/modules/command.enum";
import {
  ApplicationError,
  MachineError,
} from "@/api/events/modules/module-tracking";
import { useSendCommand } from "@/api/modules/mutations";

import ErrorModuleItem from "./error-module-item";

const ErrorListModal = ({
  areaId,
  open,
  setOpen,
  errors,
  moduleAddress,
}: {
  areaId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  errors: (MachineError | ApplicationError)[];
  moduleAddress: string;
}) => {
  const { t: translator } = useTranslation();
  const { mutate: sendCommand, isPending } = useSendCommand({
    areaId,
  });

  return (
    <Dialog open={open} onClose={() => setOpen(false)} tw="relative z-10">
      <DialogBackdrop
        transition
        tw="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-[closed]:opacity-0"
      />

      <div tw="fixed inset-0 overflow-hidden">
        <div tw="absolute inset-0 overflow-hidden">
          <div tw="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <DialogPanel
              transition
              tw="pointer-events-auto relative w-screen max-w-2xl transform transition duration-500 ease-in-out data-[closed]:translate-x-full sm:duration-700"
            >
              <div tw="flex h-full flex-col bg-white py-6 shadow-xl overflow-auto">
                <div tw="px-4 sm:px-6">
                  <div tw="flex items-start justify-between">
                    <DialogTitle tw="mt-6 text-lg font-semibold text-gray-900">
                      {translator("moduleMaintenance.errorListModal.title")}
                    </DialogTitle>
                    <div tw="ml-3 flex h-7 items-center">
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        tw="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600"
                      >
                        <span tw="absolute -inset-2.5" />
                        <span tw="sr-only">{translator("global.close")}</span>
                        <XMarkIcon aria-hidden="true" tw="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div tw="flex flex-col mt-8 gap-6 px-4 sm:px-6">
                  <ul tw="flex flex-col gap-6">
                    {errors.map((error: MachineError | ApplicationError) => (
                      <li
                        tw="text-sm font-medium max-w-[580px]"
                        key={moduleAddress}
                      >
                        <ErrorModuleItem
                          moduleAddress={moduleAddress}
                          severity={error.severity}
                          severityCode={error.severityCode}
                          label={error.label}
                          doc={error.doc}
                        />
                      </li>
                    ))}
                  </ul>
                  <div tw="flex justify-end">
                    <button
                      onClick={async () => {
                        sendCommand({
                          destinationAddress: moduleAddress,
                          command: ModuleCommandEnum.RESET_ERRORS,
                        });
                        setOpen(false);
                      }}
                      disabled={isPending}
                      css={[
                        tw`flex items-center gap-2 text-sm font-medium border border-red-500 rounded-md p-2 text-red-500 
                        hover:bg-red-500 hover:text-white hover:border-white`,
                        tw`disabled:opacity-50 disabled:cursor-not-allowed`,
                      ]}
                    >
                      <RotateCcw tw="w-4 h-4" />
                      <span>
                        {translator("moduleMaintenance.actions.resetErrors")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ErrorListModal;
